/**
 * «Выгодные зоны PRO 2» — вход по снятию ликвидности у зоны.
 *
 * Владелец 21.10.2026 попросил вход качественнее, зоны и ликвидность
 * точнее; после замера (research/zones-pro/v2-*.mjs) — «делай PRO 2, но
 * в настройках возможность регулировать, по умолчанию самые удачные».
 * Нынешний PRO не меняется: это второй индикатор рядом с ним.
 *
 * Правило, удержавшееся на шести годах часовых свечей 10 монет:
 *   1. цена в зоне (дёшево для лонга, дорого для шорта — зоны те же, что у PRO);
 *   2. свеча проколола уровень карты ликвидности и закрылась обратно —
 *      стопы за уровнем сняли;
 *   3. на этой свече покупки по рынку перевесили (≥ 55 % оборота; у шорта —
 *      продажи) — давили не туда, куда кололи;
 *   4. за 8 свечей закрытие за свечой снятия (выше её максимума у лонга) —
 *      слом, пока её минимум не обновлён. Вход — по этому закрытию.
 * Стоп — за тенью свечи снятия + 0,25 ATR (не ближе 0,5 ATR), цель — 2R.
 * Замер с этим правилом: +0,20R на сделку с издержками 0,06 %, 189 сделок
 * за 2020–2026, 2021–2022 в минусе; на 15 минутах −0,10R. Это и
 * показывает табло — статистикой по загруженной истории, без обещаний.
 *
 * Уровень карты считается известным через 3 свечи после своего бара:
 * экстремум подтверждается тремя свечами справа, а карта помечает его
 * временем самого экстремума. Без этой поправки история рисовала бы
 * снятия уровней, о которых в тот момент никто не знал.
 */
/** Умолчания — лучшие по замеру 21.10.2026. */
export const PRO2_DEFAULTS = {
    side: 'both',
    level: 'map',
    swingBars: 20,
    confirm: true,
    confirmBars: 8,
    push: 0.55,
    volume: null,
    flush: false,
    trend: false,
    target: 2,
    stopPad: 0.25,
    hold: 48,
    cooldown: 10,
    cost: 0.06,
};
const KNOWN_AFTER = 3;
const ATR_LENGTH = 14;
const VOLUME_WINDOW = 20;
const STEP_4H = 4 * 3_600_000;
/** ATR по Уайлдеру — как в замере. */
function wilderAtr(candles) {
    const out = new Float64Array(candles.length).fill(Number.NaN);
    let sum = 0;
    let value = Number.NaN;
    for (let i = 1; i < candles.length; i++) {
        const bar = candles[i];
        const prev = candles[i - 1];
        const range = Math.max(bar.high - bar.low, Math.abs(bar.high - prev.close), Math.abs(bar.low - prev.close));
        if (i <= ATR_LENGTH) {
            sum += range;
            if (i === ATR_LENGTH)
                value = sum / ATR_LENGTH;
        }
        else
            value = (value * (ATR_LENGTH - 1) + range) / ATR_LENGTH;
        out[i] = value;
    }
    return out;
}
/** Направление 4ч на каждой свече: знак последней закрытой 4ч свечи против EMA50 по 4ч закрытиям; 0 — ещё не знаем. */
function trendSeries(candles) {
    const out = new Int8Array(candles.length);
    const k = 2 / 51;
    let bucket = null;
    let close = 0;
    let ema = null;
    let count = 0;
    let sign = 0;
    for (let i = 0; i < candles.length; i++) {
        const b = Math.floor(candles[i].time / STEP_4H);
        if (bucket !== null && b !== bucket) {
            ema = ema === null ? close : ema + k * (close - ema);
            count += 1;
            sign = count >= 50 ? (close > ema ? 1 : -1) : 0;
        }
        bucket = b;
        close = candles[i].close;
        out[i] = sign;
    }
    return out;
}
/** Последнее значение ряда не позже времени. */
function valueAtOrBefore(points, time, from) {
    while (from.index + 1 < points.length && points[from.index + 1].at <= time)
        from.index += 1;
    const point = points[from.index];
    return point && point.at <= time ? point.value : null;
}
export function pro2State(input, options = PRO2_DEFAULTS) {
    const { candles, zones, levels } = input;
    const n = candles.length;
    const empty = { marks: [], waiting: [], stats: { trades: 0, wins: 0, total: 0, longs: 0, shorts: 0 }, hasShare: input.buyShare.length > 0 };
    if (n < 30)
        return empty;
    const last = input.livePartial ? n - 2 : n - 1;
    const atr = wilderAtr(candles);
    const trend = options.trend ? trendSeries(candles) : null;
    const indexOf = new Map();
    candles.forEach((bar, i) => indexOf.set(bar.time, i));
    /* Снятые уровни карты по свече снятия — только те, о которых к ней уже знали. */
    const sweptAt = new Map();
    if (options.level === 'map') {
        for (const level of levels) {
            if (level.to === null)
                continue;
            const to = indexOf.get(level.to);
            const from = indexOf.get(level.from);
            if (to === undefined || from === undefined || from + KNOWN_AFTER > to - 1)
                continue;
            if (level.evictedAt !== null) {
                const evicted = indexOf.get(level.evictedAt);
                if (evicted !== undefined && evicted <= to)
                    continue;
            }
            const list = sweptAt.get(to) ?? [];
            list.push(level);
            sweptAt.set(to, list);
        }
    }
    /* Объём, доля покупок, открытый интерес по барам. */
    const avgVolume = new Float64Array(n).fill(Number.NaN);
    {
        let sum = 0;
        for (let i = 0; i < n; i++) {
            if (i >= VOLUME_WINDOW)
                avgVolume[i] = sum / VOLUME_WINDOW;
            sum += candles[i].volume;
            if (i >= VOLUME_WINDOW)
                sum -= candles[i - VOLUME_WINDOW].volume;
        }
    }
    const shareAt = new Map(input.buyShare.map((point) => [point.at, point.value]));
    const oi = [];
    {
        const cursor = { index: 0 };
        for (const bar of candles)
            oi.push(input.openInterest.length > 0 ? valueAtOrBefore(input.openInterest, bar.time, cursor) : null);
    }
    const oiChange = oi.map((value, i) => (i >= 4 && value !== null && oi[i - 4] != null && oi[i - 4] > 0 ? value / oi[i - 4] - 1 : null));
    const oiSigmaAt = (i) => {
        let s = 0;
        let s2 = 0;
        let k = 0;
        for (let j = Math.max(0, i - 100); j < i; j++) {
            const v = oiChange[j];
            if (v == null)
                continue;
            s += v;
            s2 += v * v;
            k += 1;
        }
        return k > 50 ? Math.sqrt(Math.max(0, s2 / k - (s / k) ** 2)) : null;
    };
    const inBuy = (i) => {
        const top = zones.buyOuterTop[i];
        const bottom = zones.buyExtremeBottom[i];
        const low = candles[i].low;
        return top != null && bottom != null && low <= top && low >= bottom;
    };
    const inSell = (i) => {
        const bottom = zones.sellOuterBottom[i];
        const top = zones.sellExtremeTop[i];
        const high = candles[i].high;
        return bottom != null && top != null && high >= bottom && high <= top;
    };
    /** Прошла ли свеча снятия условия. */
    const passes = (i, long) => {
        const bar = candles[i];
        const share = shareAt.get(bar.time) ?? null;
        if (options.push !== null) {
            if (share === null)
                return { ok: false, share };
            if (long ? share < options.push : share > 1 - options.push)
                return { ok: false, share };
        }
        const ratio = avgVolume[i] > 0 ? bar.volume / avgVolume[i] : Number.NaN;
        if (options.volume !== null && !(ratio >= options.volume))
            return { ok: false, share };
        if (options.flush) {
            const change = oiChange[i];
            const sigma = oiSigmaAt(i);
            if (change == null || sigma === null || !(sigma > 0) || !(change <= -sigma) || !(ratio >= 1.5))
                return { ok: false, share };
        }
        if (trend && trend[i] !== (long ? 1 : -1))
            return { ok: false, share };
        return { ok: true, share };
    };
    /** Сделка от входа: стоп раньше цели в одной свече; не успела закончиться — «идёт». */
    const outcomeOf = (at, long, entry, stop, target) => {
        const sign = long ? 1 : -1;
        const risk = sign * (entry - stop);
        const cost = (entry * options.cost) / 100 / risk;
        const end = Math.min(last, at + options.hold);
        for (let j = at + 1; j <= end; j++) {
            const bar = candles[j];
            if (long ? bar.low <= stop : bar.high >= stop) {
                const exit = long ? Math.min(stop, bar.open) : Math.max(stop, bar.open);
                return { r: (sign * (exit - entry)) / risk - cost, kind: 'stop', exitTime: bar.time };
            }
            if (long ? bar.high >= target : bar.low <= target)
                return { r: options.target - cost, kind: 'target', exitTime: bar.time };
        }
        const exitBar = candles[end];
        return { r: (sign * (exitBar.close - entry)) / risk - cost, kind: end === at + options.hold ? 'time' : 'open', exitTime: exitBar.time };
    };
    const marks = [];
    const lastMark = { long: -Infinity, short: -Infinity };
    const pending = { long: null, short: null };
    const enter = (i, long, sweepAt, sweepPrice, extreme, share) => {
        const key = long ? 'long' : 'short';
        if (i - lastMark[key] < options.cooldown)
            return;
        const a = atr[i];
        if (!(a > 0))
            return;
        const sign = long ? 1 : -1;
        const entry = candles[i].close;
        const raw = long ? extreme - options.stopPad * a : extreme + options.stopPad * a;
        const risk = Math.max(sign * (entry - raw), 0.5 * a);
        const stop = entry - sign * risk;
        const target = entry + sign * options.target * risk;
        lastMark[key] = i;
        marks.push({ time: candles[i].time, long, entry, stop, target, sweepTime: candles[sweepAt].time, sweepPrice, share, outcome: outcomeOf(i, long, entry, stop, target) });
    };
    const START = 30;
    for (let i = START; i <= last; i++) {
        const bar = candles[i];
        for (const long of [true, false]) {
            if (options.side === 'long' && !long)
                continue;
            if (options.side === 'short' && long)
                continue;
            const key = long ? 'long' : 'short';
            // Ждём слом после прошлого снятия.
            const wait = pending[key];
            if (wait) {
                if (long ? bar.low < wait.extreme : bar.high > wait.extreme)
                    pending[key] = null;
                else if (long ? bar.close > wait.trigger : bar.close < wait.trigger) {
                    if (wait.ok)
                        enter(i, long, wait.at, wait.price, wait.extreme, wait.share);
                    pending[key] = null;
                }
                else if (i - wait.at >= options.confirmBars)
                    pending[key] = null;
            }
            if (!(long ? inBuy(i) : inSell(i)))
                continue;
            // Снятие: прокол уровня и закрытие обратно.
            let price = null;
            if (options.level === 'map') {
                for (const level of sweptAt.get(i) ?? []) {
                    const hit = long ? level.side === 'below' && bar.low < level.price && bar.close > level.price : level.side === 'above' && bar.high > level.price && bar.close < level.price;
                    if (hit)
                        price = price === null ? level.price : long ? Math.min(price, level.price) : Math.max(price, level.price);
                }
            }
            else if (i > options.swingBars) {
                let edge = long ? Infinity : -Infinity;
                for (let k = 1; k <= options.swingBars; k++)
                    edge = long ? Math.min(edge, candles[i - k].low) : Math.max(edge, candles[i - k].high);
                if (long ? bar.low < edge && bar.close > edge : bar.high > edge && bar.close < edge)
                    price = edge;
            }
            if (price === null)
                continue;
            const check = passes(i, long);
            const extreme = long ? bar.low : bar.high;
            if (options.confirm)
                pending[key] = { at: i, trigger: long ? bar.high : bar.low, extreme, ok: check.ok, share: check.share, price };
            else if (check.ok)
                enter(i, long, i, price, extreme, check.share);
        }
    }
    const waiting = [];
    for (const long of [true, false]) {
        const wait = pending[long ? 'long' : 'short'];
        if (!wait)
            continue;
        waiting.push({ long, sweepTime: candles[wait.at].time, sweepPrice: wait.price, trigger: wait.trigger, extreme: wait.extreme, barsLeft: Math.max(0, options.confirmBars - (last - wait.at)), passes: wait.ok, share: wait.share });
    }
    const closed = marks.filter((mark) => mark.outcome.kind !== 'open');
    return {
        marks,
        waiting,
        hasShare: input.buyShare.length > 0,
        stats: {
            trades: closed.length,
            wins: closed.filter((mark) => mark.outcome.r > 0).length,
            total: closed.reduce((sum, mark) => sum + mark.outcome.r, 0),
            longs: closed.filter((mark) => mark.long).length,
            shorts: closed.filter((mark) => !mark.long).length,
        },
    };
}
/* ------------------------------------------------------------ настройки */
/** Выбор «риск : прибыль»: 1 … 5 или «выкл.» — только стрелка сигнала. */
export const PRO2_RR = ['off', '1', '1.5', '2', '2.5', '3', '4', '5'];
/**
 * Цель в R. При «выкл.» сделка всё равно считается с целью 2R — так
 * сторож и табло знают, чем кончился сигнал, — но на графике и в
 * оповещении стоп и цель не показываются.
 */
function targetOf(p) {
    const rr = p['rr'];
    if (typeof rr === 'string' && rr !== 'off' && Number.isFinite(Number(rr)))
        return Math.min(10, Math.max(0.5, Number(rr)));
    return PRO2_DEFAULTS.target;
}
/** Показывать ли стоп и цель — или только стрелку сигнала. */
export function pro2PlanShown(p) {
    return p['rr'] !== 'off';
}
/**
 * Настройки из параметров индикатора — одним разбором для графика и
 * сервера: отметка на графике и оповещение обязаны считаться одинаково.
 */
export function pro2OptionsFrom(p) {
    const num = (key, fallback, min, max) => {
        const value = p[key];
        return typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
    };
    const flag = (key, fallback) => (typeof p[key] === 'boolean' ? p[key] : fallback);
    const side = p['side'] === 'long' || p['side'] === 'short' ? p['side'] : 'both';
    const D = PRO2_DEFAULTS;
    return {
        side,
        level: p['sweepLevel'] === 'swing' ? 'swing' : 'map',
        swingBars: Math.round(num('swingBars', D.swingBars, 3, 200)),
        confirm: flag('confirm', D.confirm),
        confirmBars: Math.round(num('confirmBars', D.confirmBars, 1, 50)),
        push: flag('pushOn', true) ? num('push', (D.push ?? 0.55) * 100, 50, 90) / 100 : null,
        volume: flag('volumeOn', false) ? num('volume', 2, 0.5, 10) : null,
        flush: flag('flush', D.flush),
        trend: flag('trend', D.trend),
        target: targetOf(p),
        stopPad: num('stopPad', D.stopPad, 0, 3),
        hold: Math.round(num('hold', D.hold, 2, 1000)),
        cooldown: D.cooldown,
        cost: num('cost', D.cost, 0, 2),
    };
}
//# sourceMappingURL=zones-pro2.js.map